import { FullscreenModal, Toast } from "shared";
import { getAssetModalTitle, buildInvestmentPayload } from "../../utils";
import InvestmentForm from "./InvestmentForm";
import { InvestmentFormValues } from "./forms";
import {
  useStoreInvestment,
  useUploadInvestmentFile,
  useAttachInvestmentFile,
} from "hooks/investments";
import { useGetMe } from "hooks/auth";
import { TOAST_TYPES_ENUM } from "enums";
import "./AddInvestmentModal.scss";

interface AddInvestmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSlug: string | null;
  isAdmin: boolean;
  mode?: "add" | "edit";
}

const AddInvestmentModal = ({
  isOpen,
  onClose,
  selectedSlug,
  isAdmin,
  mode = "add",
}: AddInvestmentModalProps) => {
  const title = getAssetModalTitle(selectedSlug, isAdmin, mode);
  const { mutateAsync: storeInvestment, isPending } = useStoreInvestment();
  const { mutateAsync: uploadFile } = useUploadInvestmentFile();
  const { mutateAsync: attachFile } = useAttachInvestmentFile();
  const { data: me } = useGetMe();

  const handleSubmit = async (values: InvestmentFormValues) => {
    if (!selectedSlug) return;
    const { contractFile, photoFiles, ...rest } = values as any;
    const payload = buildInvestmentPayload(
      rest as Record<string, unknown>,
      selectedSlug,
    );
    await storeInvestment(payload as any, {
      onSuccess: async (data) => {
        const userId: string | number =
          (me as any)?.user?.id ?? (me as any)?.id ?? "";

        if (
          Array.isArray(contractFile) &&
          contractFile.length > 0 &&
          data?.uuid
        ) {
          const contracts = contractFile.slice(0, 10);
          await Promise.allSettled(
            contracts.map(async (file: File) => {
              try {
                const uploaded = await uploadFile({
                  file,
                  type: "attachment",
                  parentId: userId,
                });
                const fileUuid = Array.isArray(uploaded)
                  ? uploaded[0]?.uuid
                  : (uploaded as any)?.uuid;
                if (fileUuid) {
                  await attachFile({
                    investmentUuid: data.uuid,
                    fileUuid,
                    role: "contract",
                  });
                }
              } catch {
                // pojedynczy plik nie blokuje
              }
            }),
          );
        }

        if (Array.isArray(photoFiles) && photoFiles.length > 0 && data?.uuid) {
          const photos = photoFiles.slice(0, 10);
          await Promise.allSettled(
            photos.map(async (photo: File) => {
              try {
                const uploaded = await uploadFile({
                  file: photo,
                  type: "image",
                  parentId: userId,
                });
                const fileUuid = Array.isArray(uploaded)
                  ? uploaded[0]?.uuid
                  : (uploaded as any)?.uuid;
                if (fileUuid) {
                  await attachFile({
                    investmentUuid: data.uuid,
                    fileUuid,
                    role: "photo",
                  });
                }
              } catch {
                // pojedyncze zdjęcie nie blokuje
              }
            }),
          );
        }

        Toast({
          message: "Aktywo dodane pomyślnie.",
          type: TOAST_TYPES_ENUM.SUCCESS,
          id: "store-investment",
        });
        onClose();
      },
    });
  };

  if (!selectedSlug) return null;

  return (
    <FullscreenModal isOpen={isOpen} onClose={onClose}>
      <div className="add-investment-modal">
        <h2 className="fullscreen-modal__header">{title}</h2>
        <InvestmentForm
          slug={selectedSlug}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isLoading={isPending}
          submitLabel={mode === "add" ? "Potwierdź" : "Zapisz"}
        />
      </div>
    </FullscreenModal>
  );
};

export default AddInvestmentModal;
